import { useState } from "react";
import type { DateRange } from "react-day-picker";

import { useStore } from "@nanostores/react";
import { $selectedInvestor } from "@vega-api/custom/stores/investors.store";
import {} from "@vega-api/custom/utils/usePagination";
import { useUserProfile } from "@vega-api/extended/auth/hooks";
import { useGetLoginHistory } from "@vega-api/generated/device-v1/device-v1";
import { type GetLoginHistoryParams } from "@vega-api/generated/types/getLoginHistoryParams";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { handleToDateAndFromDate } from "@vega-core/utils/formatDate";

export const useDeviceLoginData = () => {
  const paginationState = usePaginationState();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const { data: profile, isLoading: isUserProfileLoading } = useUserProfile();
  const selectedInvestor = useStore($selectedInvestor);

  const dateResult = dateRange
    ? handleToDateAndFromDate(dateRange.from, dateRange.to)
    : { from: undefined, to: undefined };

  const apiParams: GetLoginHistoryParams = {
    userId: selectedInvestor?.id || profile?.id || "",
    page: paginationState.currentPage,
    pageSize: paginationState.currentPageSize,
    ...(dateResult.from && { from: dateResult.from }),
    ...(dateResult.to && { to: dateResult.to }),
  };

  const {
    data: loginHistoryResponse,
    isLoading: isLoginHistoryLoading,
    refetch,
  } = useGetLoginHistory(apiParams, {
    query: {
      enabled: !!(selectedInvestor?.id || profile?.id),
    },
  });

  // Handle the response structure - it should be ApiResponseDeviceLogins
  // with { data: DeviceLogin[], meta: Meta }
  // But handle cases where response might be structured differently
  const loginHistory = Array.isArray(loginHistoryResponse)
    ? loginHistoryResponse
    : (loginHistoryResponse?.data ?? []);
  const meta = Array.isArray(loginHistoryResponse)
    ? undefined
    : loginHistoryResponse?.meta;

  return {
    loginHistory,
    meta,
    isLoading: isUserProfileLoading || isLoginHistoryLoading,
    currentPage: paginationState.currentPage,
    dateRange,
    handleDateRangeChange: setDateRange,
    handlePageChange: paginationState.setCurrentPage,
    refetch,
    currentPageSize: paginationState.currentPageSize,
    setCurrentPageSize: paginationState.setCurrentPageSize,
  };
};
